import "./notifications-grid.css";

import type { CompositeFilterDescriptor } from "@tql/ui-components-react/kendo/data-query";
import type {
	GridCustomCellProps,
	GridPageChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { edit_05, trash_01 } from "@tql/ui-components-react/untitled/icons";
import { useMemo } from "react";

import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { useAutoResizeColumns } from "~/shared/hooks/use-auto-resize-columns.ts";
import type { UnifiedNotificationRecipient } from "~/shared/schemas/shipment-notification-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS } from "~/shared/utils/default-grid-pager-settings.ts";

type NotificationsGridProps = {
	readonly currentUserEmail?: string;
	readonly data: UnifiedNotificationRecipient[];
	readonly filter?: CompositeFilterDescriptor;
	readonly gridKey?: number;
	readonly isAdmin?: boolean;
	readonly isLoading?: boolean;
	readonly onDelete?: (item: UnifiedNotificationRecipient) => void;
	readonly onEdit?: (item: UnifiedNotificationRecipient) => void;
	readonly onFilterChange?: (event: {
		filter: CompositeFilterDescriptor | null;
	}) => void;
	readonly onPageChange?: (event: GridPageChangeEvent) => void;
	readonly skip?: number;
	readonly take?: number;
};

export const NotificationsGrid = ({
	currentUserEmail,
	data,
	filter,
	gridKey,
	isAdmin,
	isLoading,
	onDelete,
	onEdit,
	onFilterChange,
	onPageChange,
	skip,
	take,
}: NotificationsGridProps) => {
	const gridData = useMemo(
		() =>
			data.map((r) => ({
				...r,
				user: `${r.firstName} ${r.lastName}`,
			})),
		[data],
	);

	const columns = useMemo(
		(): BoardColumn[] => [
			{
				field: "user",
				title: "User",
				show: true,
				filterable: true,
			},
			{
				field: "email",
				title: "Email",
				show: true,
				filterable: true,
				editable: false,
			},
			{
				field: "subscribedToAll",
				title: "Subscribed to All",
				show: true,
				filterable: false,
				editable: false,
				cells: {
					data: (props: GridCustomCellProps) => {
						const dataItem = props.dataItem as
							| UnifiedNotificationRecipient
							| undefined;
						return (
							<GridTd tdProps={props.tdProps}>
								{dataItem?.subscribedToAll ? "Yes" : "No"}
							</GridTd>
						);
					},
				},
			},
			{
				field: "actions",
				title: "Actions",
				show: true,
				filterable: false,
				sortable: false,
				editable: false,
				cells: {
					data: (props: GridCustomCellProps) => {
						const dataItem = props.dataItem as
							| UnifiedNotificationRecipient
							| undefined;
						const canModify =
							isAdmin === true ||
							dataItem?.email.toLowerCase() === currentUserEmail?.toLowerCase();
						return (
							<GridTd tdProps={props.tdProps}>
								<div className={"notifications-row-actions"}>
									<Button
										data-testid={"notifications-row-edit-button"}
										disabled={!canModify}
										onClick={() => dataItem && onEdit?.(dataItem)}
										svgIcon={edit_05}
										variant={"tertiary"}
									/>
									<Button
										data-testid={"notifications-row-delete-button"}
										disabled={!canModify}
										onClick={() => dataItem && onDelete?.(dataItem)}
										svgIcon={trash_01}
										variant={"dangerTertiary"}
									/>
								</div>
							</GridTd>
						);
					},
				},
			},
		],
		[onEdit, onDelete, isAdmin, currentUserEmail],
	);

	const { wrapperRef, distributedColumns } = useAutoResizeColumns(columns);

	return (
		<div ref={wrapperRef} className={"notifications-grid-wrapper"}>
			<TraxGrid
				key={gridKey}
				className={"notifications-grid"}
				columns={convertToGridColumnProps(distributedColumns)}
				data={gridData}
				dataItemKey={"id"}
				filter={filter}
				filterable={true}
				loading={isLoading}
				onFilterChange={onFilterChange}
				onPageChange={onPageChange}
				pageable={DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS}
				skip={skip}
				style={{ height: "100%" }}
				take={take}
			/>
		</div>
	);
};
